import axios from 'axios';
import { API_BASE_URL } from '../utils/constants';
import { getAuthToken } from '../utils/storage';
import { extractApiError } from '../utils/apiError';

import { applyWarehouseFilter, warehouseParamsSerializer } from '../utils/warehouseParams';
import { registerClearable } from '../utils/cacheRegistry';

// Axios instance for warehouses API
const warehousesAPI = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  headers: { 'Content-Type': 'application/json' },
  paramsSerializer: warehouseParamsSerializer,
});

// Attach auth token if available
warehousesAPI.interceptors.request.use(
  (config) => {
    const token = getAuthToken();

    if (token) config.headers.Authorization = `Bearer ${token}`;
    return applyWarehouseFilter(config);
  },
  (error) => Promise.reject(error)
);

// Redirect on auth failure
warehousesAPI.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('access_token');
      localStorage.removeItem('user_data');
      sessionStorage.removeItem('auth_token');
      sessionStorage.removeItem('access_token');
      sessionStorage.removeItem('user_data');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

const BASE_URL = '/warehouses';

// Caching helpers (memory + sessionStorage)
const CACHE_PREFIX = 'erp_warehouses_cache_v1';
const DEFAULT_TTL_MS = 10 * 60 * 1000; // 10 minutes

const memoryCache = new Map();

function cacheKey(key) {
  return `${CACHE_PREFIX}::${key}`;
}

function readCache(key) {
  try {
    const raw = sessionStorage.getItem(cacheKey(key));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.expires) return null;
    if (Date.now() > parsed.expires) {
      sessionStorage.removeItem(cacheKey(key));
      return null;
    }
    return parsed.value;
  } catch (e) {
    return null;
  }
}

function writeCache(key, value, ttl = DEFAULT_TTL_MS) {
  try {
    sessionStorage.setItem(cacheKey(key), JSON.stringify({ value, expires: Date.now() + ttl }));
  } catch (e) { }
}

function readMemoryCache(key) {
  const entry = memoryCache.get(key);
  if (!entry) return null;
  if (Date.now() > entry.expires) {
    memoryCache.delete(key);
    return null;
  }
  return entry.value;
}

function writeMemoryCache(key, value, ttl = DEFAULT_TTL_MS) {
  memoryCache.set(key, { value, expires: Date.now() + ttl });
}

function deleteCacheEntry(key) {
  try { memoryCache.delete(key); } catch (e) { }
  try { sessionStorage.removeItem(cacheKey(key)); } catch (e) { }
}

// A cached full list is either a bare array or a paginated `{ items, total }`
// envelope (the newer list endpoints). The modifier used to run only on bare
// arrays, so an envelope kept serving the stale row after an update/delete (P1-4).
function applyListModifier(value, modifierFn) {
  if (Array.isArray(value)) return modifierFn(value.slice());
  if (value && Array.isArray(value.items)) {
    const items = modifierFn(value.items.slice());
    const delta = items.length - value.items.length;
    const total = typeof value.total === 'number' ? Math.max(0, value.total + delta) : value.total;
    return { ...value, items, total };
  }
  return null;
}

function updateFullListCachesWithModifier(modifierFn) {
  try {
    for (const [key, entry] of memoryCache.entries()) {
      if (typeof key === 'string' && key.startsWith('all::')) {
        const next = applyListModifier(entry?.value, modifierFn);
        if (next !== null) writeMemoryCache(key, next);
      }
    }
  } catch (e) { }

  try {
    for (let i = 0; i < sessionStorage.length; i++) {
      const sk = sessionStorage.key(i);
      if (!sk) continue;
      if (sk.startsWith(`${CACHE_PREFIX}::all::`)) {
        const raw = sessionStorage.getItem(sk);
        if (!raw) continue;
        try {
          const parsed = JSON.parse(raw);
          const next = applyListModifier(parsed?.value, modifierFn);
          if (next !== null) {
            parsed.value = next;
            sessionStorage.setItem(sk, JSON.stringify(parsed));
          }
        } catch (e) { }
      }
    }
  } catch (e) { }
}



const warehouseService = {
  async getAllWarehouses(params = {}, options = {}) {
    const { force = false } = options;
    const key = `all::${JSON.stringify(params || {})}`;
    if (!force) {
      const mem = readMemoryCache(key);
      if (mem) return { success: true, data: mem };
      const sess = readCache(key);
      if (sess) { writeMemoryCache(key, sess); return { success: true, data: sess }; }
    }

    try {
      const query = new URLSearchParams();
      if (params.skip !== undefined) query.append('skip', params.skip);
      if (params.limit !== undefined) query.append('limit', params.limit);
      if (params.search) query.append('search', params.search);

      const url = query.toString() ? `${BASE_URL}?${query.toString()}` : BASE_URL;
      const response = await warehousesAPI.get(url);
      const data = response.data;
      writeMemoryCache(key, data);
      writeCache(key, data);
      return { success: true, data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  async getWarehouseById(warehouseId, options = {}) {
    const { force = false } = options;
    const key = `byId::${warehouseId}`;
    if (!force) {
      const mem = readMemoryCache(key);
      if (mem) return { success: true, data: mem };
      const sess = readCache(key);
      if (sess) { writeMemoryCache(key, sess); return { success: true, data: sess }; }
      // fallback: search in a cached full-list
      const fullKey = `all::${JSON.stringify({})}`;
      const fullMem = readMemoryCache(fullKey) || readCache(fullKey);
      if (fullMem && Array.isArray(fullMem)) {
        const found = fullMem.find((i) => String(i.id) === String(warehouseId));
        if (found) { writeMemoryCache(key, found); writeCache(key, found); return { success: true, data: found }; }
      }
    }

    try {
      const response = await warehousesAPI.get(`${BASE_URL}/${warehouseId}`);
      const data = response.data;
      writeMemoryCache(key, data);
      writeCache(key, data);
      return { success: true, data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  async createWarehouse(payload) {
    try {
      const response = await warehousesAPI.post(BASE_URL, payload);
      const created = response.data;

      // prepend to cached full-lists
      updateFullListCachesWithModifier((arr) => {
        const exists = arr.some((i) => String(i.id) === String(created.id));
        if (exists) return arr;
        return [created, ...arr];
      });

      try { const key = `byId::${created.id}`; writeMemoryCache(key, created); writeCache(key, created); } catch (e) { }

      return { success: true, data: created };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  async updateWarehouse(warehouseId, payload) {
    try {
      const response = await warehousesAPI.put(`${BASE_URL}/${warehouseId}`, payload);
      const updated = response.data;

      updateFullListCachesWithModifier((arr) => arr.map((i) => (String(i.id) === String(warehouseId) ? updated : i)));

      try { const key = `byId::${warehouseId}`; writeMemoryCache(key, updated); writeCache(key, updated); } catch (e) { }

      return { success: true, data: updated };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  async deleteWarehouse(warehouseId) {
    try {
      const response = await warehousesAPI.delete(`${BASE_URL}/${warehouseId}`, { params: { hard_delete: true } });

      updateFullListCachesWithModifier((arr) => arr.filter((i) => String(i.id) !== String(warehouseId)));
      try { deleteCacheEntry(`byId::${warehouseId}`); } catch (e) { }

      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  clearCache() {
    try {
      // Memory keys are "all::…" / "byId::…" — the old substring match never hit, so
      // the in-memory list outlived clearCache() and kept serving stale rows (P1-4).
      memoryCache.clear();
      for (let i = sessionStorage.length - 1; i >= 0; i--) {
        const k = sessionStorage.key(i);
        if (k && k.startsWith(CACHE_PREFIX)) sessionStorage.removeItem(k);
      }
    } catch (e) { }
  }
};

// Cleared on logout together with the other services (policy C16).
registerClearable(() => warehouseService.clearCache());

export default warehouseService;
